import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { LoginComponent } from './core/components/login/login.component';
import { NotRegisteredComponent } from './core/components/not-registered/not-registered.component';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { devLog } from './core/utils/generic-utils';
import { CrossSellingComponent } from './features/cross-selling/cross-selling/cross-selling.component';
import { ForbiddenComponent } from './features/forbidden/forbidden.component';
import { GlobalSettingsGuard } from './features/global-settings/guards/global-settings.guard';
import { OrderTrackingGuardService } from './features/orders/services/order-tracking-guard.service';
import { PendingApprovalGuard } from './features/pending-approval/guards/pending-approval.guard';
import { PendingApprovalComponent } from './features/pending-approval/pending-approval/pending-approval.component';
import { ProfileComponent } from './features/profile/profile.component';
import { SecuredSkeletonComponent } from './features/secured-skeleton/components/secured-skeleton/secured-skeleton.component';
import { ShopRedirectComponent } from './features/secured-skeleton/components/shop-redirect/shop-redirect.component';
import { CrossSellingGuard } from './features/support/services/cross-selling.guard';
import { ForbiddenGuard } from './features/support/services/forbidden.guard';
import { WarrantyGuardService } from './features/support/services/warranty-guard.service';
import { UserPermissionsGuard } from './features/user-permissions/guards/user-permissions.guard';
import { PmoDashboardGuardService } from './features/pmo-dashboard/guards/pmo-dashboard.guard';
import { SuperAdminCenterGuard } from './features/super-admin-center/guards/super-admin-center.guard';
import { LifecycleCommandGuard } from './features/lifecycle-command/guards/lifecycle-command.guard';
import { ManagedServicesGuard } from './features/managed-services/guards/managed-services.guard';
import { ConnectivityServicesGuard } from './features/connectivity-services/guards/connectivity-services.guard';

// TODO: Run tests to determine if reload retry mechanism works
const maxDyamicModuleRetries = 3;
let homeReloadRetries = 0;
let repairServicesReloadRetries = 0;
let orderTrackingReloadRetries = 0;
let userPermissionsReloadRetries = 0;
let managedServicesReloadRetries = 0;
let globalSettingsReloadRetries = 0;
let superAdminCenterReloadRetries = 0;
let pmodashboardReloadRetries = 0;
let connectivityServicesReloadRetries = 0;
let lifecycleCommandReloadRetries = 0;

const orderTrackingErrorHandler = (err) => {
  devLog({
    reason: 'Error loading repair services dynamic module',
    error: err,
    retries: orderTrackingReloadRetries,
  });

  const chunkFailedMessage = /Loading chunk [\d]+ failed/;
  if (chunkFailedMessage.test(err.message) && orderTrackingReloadRetries < maxDyamicModuleRetries) {
    orderTrackingReloadRetries += 1;
    console.warn(`Reloading due to chunk loading error! app-routing catch`);
    window.location.reload();
  }
  return err;
};

const routes: Routes = [
  {
    path: 'prototypes',
    loadChildren: () =>
      import('./features/prototypes/prototypes.module').then((dynamicModule) => dynamicModule.PrototypesModule),
  },
  {
    path: 'secured',
    canActivate: [MsalGuard],
    component: SecuredSkeletonComponent,
    children: [
      {
        path: 'shop',
        component: ShopRedirectComponent,
      },
      {
        path: 'forbidden/:addon',
        component: ForbiddenComponent,
        canActivate: [ForbiddenGuard],
      },
      {
        path: 'cross-selling/:addon',
        component: CrossSellingComponent,
        canActivate: [CrossSellingGuard],
      },
      {
        path: 'pending-approval/shop',
        component: PendingApprovalComponent,
        canActivate: [PendingApprovalGuard],
      },
      {
        path: 'profile',
        component: ProfileComponent, // TODO: Transform into dynamic module if application grows
      },
      {
        path: 'global-settings',
        canActivate: [GlobalSettingsGuard],
        loadChildren: () => {
          return import('./features/global-settings/global-settings.module')
            .then((dynamicModule) => dynamicModule.GlobalSettingsModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading global settings dynamic module',
                error: err,
                retries: globalSettingsReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;

              if (chunkFailedMessage.test(err.message) && globalSettingsReloadRetries < maxDyamicModuleRetries) {
                globalSettingsReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'super-admin-center',
        canActivate: [SuperAdminCenterGuard],
        loadChildren: () => {
          return import('./features/super-admin-center/super-admin-center.module')
            .then((dynamicModule) => dynamicModule.SuperAdminCenterModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading super admin center dynamic module',
                error: err,
                retries: superAdminCenterReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;

              if (chunkFailedMessage.test(err.message) && superAdminCenterReloadRetries < maxDyamicModuleRetries) {
                superAdminCenterReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'home',
        loadChildren: () => {
          return import('./features/secured-home/secured-home.module')
            .then((dynamicModule) => {
              return dynamicModule.SecuredHomeModule;
            })
            .catch((err) => {
              devLog({
                reason: 'Error loading home dynamic module',
                error: err,
                retries: homeReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && homeReloadRetries < maxDyamicModuleRetries) {
                homeReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'support',
        canActivate: [WarrantyGuardService],
        loadChildren: () => {
          return import('./features/support/support.module')
            .then((dynamicModule) => dynamicModule.SupportModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading repair services dynamic module',
                error: err,
                retries: repairServicesReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && repairServicesReloadRetries < maxDyamicModuleRetries) {
                repairServicesReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'orders',
        canActivate: [OrderTrackingGuardService],
        loadChildren: () => {
          return import('./features/orders-v3/orders-v3.module')
            .then((dynamicModule) => dynamicModule.OrdersV3Module)
            .catch(orderTrackingErrorHandler);
        },
      },
      {
        path: 'user-permissions',
        canActivate: [UserPermissionsGuard],
        loadChildren: () => {
          return import('./features/user-permissions/user-permissions.module')
            .then((dynamicModule) => dynamicModule.UserPermissionsModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading user permissions dynamic module',
                error: err,
                retries: userPermissionsReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && userPermissionsReloadRetries < maxDyamicModuleRetries) {
                userPermissionsReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'managed-services',
        canActivate: [ManagedServicesGuard],
        loadChildren: () => {
          return import('./features/managed-services/managed-services.module')
            .then((dynamicModule) => dynamicModule.ManagedServicesModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading managed service dynamic module',
                error: err,
                retries: managedServicesReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && managedServicesReloadRetries < maxDyamicModuleRetries) {
                managedServicesReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }

              return err;
            });
        },
      },
      {
        path: 'connectivity-services',
        canActivate: [ConnectivityServicesGuard],
        loadChildren: () => {
          return import('./features/connectivity-services/connectivity-services.module')
            .then((dynamicModule) => dynamicModule.ConnectivityServicesModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading connectivity services dynamic module',
                error: err,
                retries: connectivityServicesReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && connectivityServicesReloadRetries < maxDyamicModuleRetries) {
                connectivityServicesReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'project-services',
        canActivate: [PmoDashboardGuardService],
        loadChildren: () => {
          return import('./features/pmo-dashboard/pmo-dashboard.module')
            .then((dynamicModule) => dynamicModule.PmoDashboardModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading Project Services dynamic module',
                error: err,
                retries: pmodashboardReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && pmodashboardReloadRetries < maxDyamicModuleRetries) {
                pmodashboardReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      {
        path: 'lifecycle-command',
        canActivate: [LifecycleCommandGuard],
        loadChildren: () => {
          return import('./features/lifecycle-command/lifecycle-command.module')
            .then((dynamicModule) => dynamicModule.LifecycleCommandModule)
            .catch((err) => {
              devLog({
                reason: 'Error loading lifecycle command dynamic module',
                error: err,
                retries: lifecycleCommandReloadRetries,
              });

              const chunkFailedMessage = /Loading chunk [\d]+ failed/;
              if (chunkFailedMessage.test(err.message) && lifecycleCommandReloadRetries < maxDyamicModuleRetries) {
                lifecycleCommandReloadRetries += 1;
                console.warn(`Reloading due to chunk loading error! app-routing catch`);
                window.location.reload();
              }
              return err;
            });
        },
      },
      { path: 'domain-lookup', redirectTo: '/secured/connectivity-services/domain-lookup', pathMatch: 'full' },
      { path: 'domain-lookup/:id', redirectTo: '/secured/connectivity-services/domain-lookup/:id', pathMatch: 'full' },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },
    ],
  },
  { path: 'login', component: LoginComponent },
  { path: 'not-registered', component: NotRegisteredComponent },
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
